import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardMedia, Typography, Box, Chip, LinearProgress,
  Avatar, AvatarGroup,
} from '@mui/material';
import { AccessTime, LocationOn, Group, Whatshot } from '@mui/icons-material';
import { colors, designTokens } from '../../theme/theme';
import { EventItem } from '../../services/eventsService';

interface Props {
  event: EventItem;
}

export default function EventCard({ event }: Props) {
  const navigate = useNavigate();

  const isProfessional = event.event_type === 'professional';
  const isMasterclass = event.event_type === 'masterclass';
  const fillPercent = Math.min((event.current_count / event.max_capacity) * 100, 100);
  const spotsText = event.is_full ? 'Full' : `${event.spots_remaining} spot${event.spots_remaining !== 1 ? 's' : ''} left`;

  const deadlineText = event.signup_deadline
    ? getCountdown(event.signup_deadline)
    : '';

  // Capacity color logic
  const capacityColor = event.is_full
    ? colors.error.main
    : event.spots_remaining <= 2
      ? colors.warning.dark
      : colors.secondary.main;

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: `all ${designTokens.transition.normal}`,
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: `${designTokens.shadow.lg}, 0 0 24px rgba(232,135,92,0.08)`,
          borderColor: 'rgba(232,135,92,0.2)',
        },
        '&:active': {
          transform: 'translateY(-2px)',
        },
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '20px',
      }}
      onClick={() => navigate(`/events/${event.id}`)}
    >
      {/* ── Venue Image with overlay badges ── */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <CardMedia
          component="img"
          loading="lazy"
          height="180"
          image={event.venue?.photo_url || 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=600'}
          alt={event.venue?.name || 'Venue'}
          sx={{
            objectFit: 'cover',
            transition: `transform ${designTokens.transition.slow}`,
            '.MuiCard-root:hover &': { transform: 'scale(1.04)' },
          }}
        />
        {/* Soft gradient overlay at bottom of image */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 60,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.08))',
          }}
        />
        {/* Type + Trending badges */}
        <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 0.5 }}>
          {event.is_trending && (
            <Chip
              icon={<Whatshot sx={{ fontSize: 14, color: 'white !important' }} />}
              label="Trending"
              size="small"
              sx={{
                bgcolor: 'rgba(255,87,34,0.95)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 26,
                borderRadius: 8,
              }}
            />
          )}
          <Chip
            label={isMasterclass ? '⭐ Master Class' : isProfessional ? 'Professional' : 'Social'}
            size="small"
            sx={{
              bgcolor: isMasterclass ? 'rgba(255,215,0,0.95)' : 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(8px)',
              color: isProfessional ? colors.primary.main : colors.accent.dark,
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 26,
              borderRadius: 8,
            }}
          />
          {event.voucher_tier === 'premium' && (
            <Chip
              label="Premium"
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(8px)',
                color: colors.secondary.dark,
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 26,
                borderRadius: 8,
              }}
            />
          )}
        </Box>
        {/* Deadline badge (top right) */}
        {deadlineText && deadlineText !== 'Closed' && (
          <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
            <Chip
              label={deadlineText}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(8px)',
                color: colors.neutral[700],
                fontWeight: 600,
                fontSize: '0.68rem',
                height: 26,
                borderRadius: 8,
              }}
            />
          </Box>
        )}
      </Box>

      <CardContent sx={{ p: 2.5, pb: '20px !important' }}>
        {/* ── Title ── */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 1,
            lineHeight: 1.3,
            fontSize: '1.05rem',
            color: '#FFFFFF',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {event.title}
        </Typography>

        {/* ── Venue ── */}
        {event.venue && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
            <LocationOn sx={{ fontSize: 15, color: colors.neutral[400] }} />
            <Typography variant="body2" sx={{ color: colors.neutral[500], fontSize: '0.82rem' }}>
              {event.venue.name}
            </Typography>
            {event.venue.courtesy_discount && (
              <Chip
                label={event.venue.courtesy_discount}
                size="small"
                sx={{
                  ml: 'auto',
                  fontSize: '0.62rem',
                  height: 20,
                  bgcolor: colors.success.subtle,
                  color: colors.success.dark,
                  fontWeight: 700,
                  borderRadius: 6,
                }}
              />
            )}
          </Box>
        )}

        {/* ── Date & Time ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
          <AccessTime sx={{ fontSize: 15, color: colors.neutral[400] }} />
          <Typography variant="body2" sx={{ color: colors.neutral[500], fontSize: '0.82rem' }}>
            {event.scheduled_date} {event.scheduled_time ? `at ${event.scheduled_time.slice(0, 5)}` : ''} · {event.duration_minutes}min
          </Typography>
        </Box>

        {/* ── Capacity Bar — Elegant, thin ── */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Group sx={{ fontSize: 15, color: colors.neutral[400] }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: colors.neutral[600] }}>
                {event.current_count}/{event.max_capacity}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: capacityColor }}
            >
              {spotsText}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={fillPercent}
            sx={{
              height: 5,
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.06)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                bgcolor: capacityColor,
                transition: 'width 0.6s ease',
              },
            }}
          />
        </Box>

        {/* ── Bottom Row: Attendees + Price ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Attendees Preview */}
          {event.attendees && event.attendees.length > 0 ? (
            <AvatarGroup
              max={4}
              sx={{
                '& .MuiAvatar-root': {
                  width: 28,
                  height: 28,
                  fontSize: '0.7rem',
                  borderWidth: 2,
                },
              }}
            >
              {event.attendees.map((a, i) => (
                <Avatar
                  key={i}
                  src={a.avatar_url || undefined}
                  sx={{ bgcolor: colors.secondary.light }}
                >
                  {a.first_name[0]}
                </Avatar>
              ))}
            </AvatarGroup>
          ) : (
            <Box />
          )}

          {/* Price */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.3,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                color: colors.primary.main,
                fontSize: '1.2rem',
                letterSpacing: '-0.02em',
              }}
            >
              {(event.voucher_tier === 'premium' ? 13 : 10) + (event.masterclass_fee || 0)}
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: colors.neutral[400],
                fontSize: '0.75rem',
              }}
            >
              AZN
              {isMasterclass && (
                <Typography component="span" sx={{ fontSize: '0.6rem', display: 'block', color: colors.warning.dark }}>
                  incl. class fee
                </Typography>
              )}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function getCountdown(deadline: string): string {
  const diff = new Date(deadline).getTime() - Date.now();
  if (diff <= 0) return 'Closed';
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h left`;
  if (hours > 0) return `${hours}h left`;
  const mins = Math.floor(diff / (1000 * 60));
  return `${mins}m left`;
}
